<template>
  <div class="user-management">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">User Management</h1>
        <p class="page-description">
          Manage platform members, invite new users, and control access permissions. You can
          activate, deactivate, or permanently remove users from the system.
        </p>
      </div>
      <button class="btn-invite" @click="showInviteModal = true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <circle
            cx="8.5"
            cy="7"
            r="4"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <line
            x1="20"
            y1="8"
            x2="20"
            y2="14"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <line
            x1="23"
            y1="11"
            x2="17"
            y2="11"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Invite Member
      </button>
    </div>

    <!-- Stats Cards -->
    <div class="stats-container">
      <StatsCard label="Total Users" :value="totalUsers" variant="primary" />
      <StatsCard label="Active Users" :value="activeUsers" variant="success" />
      <StatsCard label="Inactive Users" :value="inactiveUsers" variant="warning" />
    </div>

    <!-- All Users Section -->
    <div class="users-section">
      <div class="section-header">
        <div>
          <h2 class="section-title">All Users</h2>
          <p class="section-subtitle">View and manage all registered users on the platform</p>
        </div>
        <div class="filters">
          <div class="search-input">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
                stroke="#9CA3AF"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M21 21L16.65 16.65"
                stroke="#9CA3AF"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <input v-model="searchQuery" type="text" placeholder="Search by name, email, role" />
          </div>
          <div class="filter-dropdown">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                stroke="#6B7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="12"
                cy="7"
                r="4"
                stroke="#6B7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <select v-model="filterRole">
              <option value="">All Roles</option>
              <option v-for="role in roles" :key="role.id" :value="role.name">
                {{ role.name }}
              </option>
            </select>
          </div>
          <div class="filter-dropdown">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polygon
                points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"
                stroke="#6B7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <select v-model="filterStatus">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Users Table -->
      <div v-loading="loadingList" class="users-table-container">
        <table class="users-table">
          <thead>
            <tr>
              <th class="checkbox-col">
                <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.id">
              <td class="checkbox-col">
                <input
                  type="checkbox"
                  :checked="selectedUsers.includes(user.id)"
                  @change="toggleSelectUser(user.id)"
                />
              </td>
              <td class="name-col">{{ formatName(user) }}</td>
              <td class="email-col">
                <span class="email-wrapper">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                      stroke="#6B7280"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M22 6L12 13L2 6"
                      stroke="#6B7280"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {{ user.email }}
                </span>
              </td>
              <td class="role-col">
                <span class="role-wrapper">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                      stroke="#6B7280"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="7"
                      r="4"
                      stroke="#6B7280"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {{ user.group || "N/A" }}
                </span>
              </td>
              <td class="status-col">
                <span :class="['status-badge', isUserActive(user) ? 'active' : 'inactive']">
                  {{ isUserActive(user) ? "Active" : "Inactive" }}
                </span>
              </td>
              <td class="login-col">
                <span class="login-wrapper">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                      stroke="#6B7280"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="7"
                      r="4"
                      stroke="#6B7280"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {{ formatDate(user.last_login) }}
                </span>
              </td>
              <td class="action-col">
                <ActionDropdown
                  :user="user"
                  @edit="openEditModal"
                  @deactivate="handleDeactivate"
                  @delete="openDeleteModal"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!filteredUsers.length && !loadingList" class="empty-state">
          <p>No users found matching your criteria.</p>
        </div>
      </div>

      <div class="table-footer">
        <span class="showing-text"
          >Showing {{ filteredUsers.length }} of {{ totalUsers }} total users</span
        >
      </div>
    </div>

    <!-- Modals -->
    <InviteUserModal
      :visible="showInviteModal"
      :roles="roles"
      :loading="inviteLoading"
      @close="showInviteModal = false"
      @confirm="handleInvite"
    />

    <EditUserModal
      :visible="showEditModal"
      :user="selectedUser"
      :roles="roles"
      :loading="editLoading"
      @close="closeEditModal"
      @confirm="handleEdit"
    />

    <DeleteUserModal
      :visible="showDeleteModal"
      :user="selectedUser"
      :loading="deleteLoading"
      @close="closeDeleteModal"
      @confirm="handleDelete"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import StatsCard from "./components/StatsCard.vue";
import ActionDropdown from "./components/ActionDropdown.vue";
import InviteUserModal from "./components/InviteUserModal.vue";
import EditUserModal from "./components/EditUserModal.vue";
import DeleteUserModal from "./components/DeleteUserModal.vue";
import userResource from "../../api/userResource";

export default {
  name: "ListUsers",
  components: {
    StatsCard,
    ActionDropdown,
    InviteUserModal,
    EditUserModal,
    DeleteUserModal,
  },
  data() {
    return {
      loadingList: true,
      searchQuery: "",
      filterRole: "",
      filterStatus: "",
      selectedUsers: [],
      showInviteModal: false,
      showEditModal: false,
      showDeleteModal: false,
      selectedUser: null,
      inviteLoading: false,
      editLoading: false,
      deleteLoading: false,
      roles: [
        { id: 0, name: "data_viewer" },
        { id: 1, name: "data manager" },
        { id: 2, name: "super admin" },
        { id: 3, name: "admin" },
      ],
    };
  },
  computed: {
    ...mapGetters(["users"]),
    totalUsers() {
      return this.users.length;
    },
    activeUsers() {
      return this.users.filter((u) => this.isUserActive(u)).length;
    },
    inactiveUsers() {
      return this.users.filter((u) => !this.isUserActive(u)).length;
    },
    filteredUsers() {
      let result = [...this.users];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter((user) => {
          const name = this.formatName(user).toLowerCase();
          const email = (user.email || "").toLowerCase();
          const role = (user.group || "").toLowerCase();
          return name.includes(query) || email.includes(query) || role.includes(query);
        });
      }

      // Role filter
      if (this.filterRole) {
        result = result.filter((user) => user.group === this.filterRole);
      }

      // Status filter
      if (this.filterStatus) {
        const isActive = this.filterStatus === "active";
        result = result.filter((user) => this.isUserActive(user) === isActive);
      }

      return result;
    },
    allSelected() {
      return (
        this.filteredUsers.length > 0 &&
        this.filteredUsers.every((u) => this.selectedUsers.includes(u.id))
      );
    },
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    ...mapActions("users", ["loadusers", "updateUser", "updateUserRole", "deleteUser"]),

    async getUsers() {
      this.loadingList = true;
      try {
        if (!this.users.length) await this.loadusers();
      } catch (error) {
        console.log(error);
        this.$notify?.error?.("Failed to load users");
      }
      this.loadingList = false;
    },

    formatName(user) {
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`;
      }
      return user.username || user.email || "Unknown";
    },

    formatDate(dateStr) {
      if (!dateStr) return "Never";
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    isUserActive(user) {
      // If is_active is undefined, default to true (registered users are active)
      return user.is_active !== false;
    },

    navigateToEditPage(user) {
      this.$router.push({ path: "/users/edit", query: { userId: user.id } });
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedUsers = [];
      } else {
        this.selectedUsers = this.filteredUsers.map((u) => u.id);
      }
    },

    toggleSelectUser(userId) {
      const index = this.selectedUsers.indexOf(userId);
      if (index > -1) {
        this.selectedUsers.splice(index, 1);
      } else {
        this.selectedUsers.push(userId);
      }
    },

    openEditModal(user) {
      this.selectedUser = user;
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.selectedUser = null;
    },

    openDeleteModal(user) {
      this.selectedUser = user;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedUser = null;
    },

    async handleInvite(userData) {
      this.inviteLoading = true;
      try {
        await userResource.invite(userData);
        this.$notify?.({
          message: `Invite sent to ${userData.email}`,
          type: "success",
        });
        this.showInviteModal = false;
      } catch (error) {
        console.error(error);
        this.$notify?.error?.("Error sending invitation");
      } finally {
        this.inviteLoading = false;
      }
    },

    async handleEdit(userData) {
      this.editLoading = true;
      try {
        const user = {
          first_name: userData.first_name,
          last_name: userData.last_name,
          username: userData.username,
        };
        await this.updateUser({ id: userData.id, user });

        // Update role if changed
        const currentRoleIndex = this.roles.findIndex((r) => r.name === this.selectedUser.group);
        if (userData.roleIndex !== currentRoleIndex) {
          await this.updateUserRole({ userId: userData.id, type: userData.roleIndex });
        }

        this.$notify?.success?.("User updated successfully");
        this.closeEditModal();
        await this.loadusers();
      } catch (error) {
        console.error(error);
        this.$notify?.error?.("Error updating user");
      } finally {
        this.editLoading = false;
      }
    },

    async handleDelete(user) {
      this.deleteLoading = true;
      try {
        await this.deleteUser(user.id);
        this.$notify?.success?.("User deleted successfully");
        this.closeDeleteModal();
      } catch (error) {
        console.error(error);
        this.$notify?.error?.("Error deleting user");
      } finally {
        this.deleteLoading = false;
      }
    },

    async handleDeactivate(user) {
      // Toggle user active status
      try {
        // Note: This would need a backend endpoint to toggle status
        this.$notify?.info?.(
          `${user.is_active ? "Deactivating" : "Activating"} user is not yet implemented`
        );
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-management {
  padding: 24px 32px;
  background: #f9fafb;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.header-content {
  max-width: 700px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
}

.page-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.btn-invite {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #1e40af;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #1e3a8a;
  }
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 24px;
}

.users-section {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
}

.section-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.filters {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  min-width: 240px;

  input {
    border: none;
    background: transparent;
    outline: none;
    font-size: 14px;
    color: #111827;
    width: 100%;

    &::placeholder {
      color: #9ca3af;
    }
  }
}

.filter-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  select {
    border: none;
    background: transparent;
    outline: none;
    font-size: 14px;
    color: #374151;
    cursor: pointer;
    padding-right: 8px;
  }
}

.users-table-container {
  overflow-x: auto;
  overflow-y: visible;
  min-height: 200px;
}

.users-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 16px 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }

  th {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    background: #f9fafb;
  }

  td {
    font-size: 14px;
    color: #111827;
  }

  tbody tr:hover {
    background: #f9fafb;
  }
}

.checkbox-col {
  width: 40px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}

.name-col {
  font-weight: 500;
}

.email-col,
.role-col,
.login-col {
  .email-wrapper,
  .role-wrapper,
  .login-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;

  &.active {
    background: #d1fae5;
    color: #059669;
  }

  &.inactive {
    background: #f3f4f6;
    color: #6b7280;
  }
}

.action-col {
  width: 60px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

.table-footer {
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 16px;
}

.showing-text {
  font-size: 14px;
  color: #6b7280;
}

@media (max-width: 768px) {
  .user-management {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
  }

  .filters {
    width: 100%;
  }

  .search-input {
    width: 100%;
  }
}
</style>
